import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { EdiNamedItem, EdiSetupEditGet, EdiSetupEditorResult, EdiSetupModalData, EdiSetupRow, EdiType } from '@app/services/edi.service';

@Component({
    selector: 'tt-edi-setup-modal',
    templateUrl: './edi-setup-modal.component.html',
    styleUrls: ['./edi-setup-modal.component.css'],
    standalone: false,
})
export class EdiSetupModalComponent implements OnDestroy {
    public page: EdiSetupEditGet = {
        p2EdisetupKeyno: 0, custNo: 0, businesscoNo: 0, custgrpId: '', ediTypeKeyno: 0,
        p2ReportdefKeyno: 0, p2ExportprocKeyno: 0, ediDestinationKeyno: 0,
        sendByEmail: '0', sendEdi: '0', remarks: '',
    };
    public editing = false;
    public saving = false;
    public error = '';
    public types: EdiType[] = [];
    public destinations: EdiNamedItem[] = [];
    public reportdefs: EdiNamedItem[] = [];
    public exportprocs: EdiNamedItem[] = [];
    private readonly fields = Object.keys(this.page);
    private readonly keyboard: Subscription;

    constructor(@Inject(MAT_DIALOG_DATA) private readonly parameters: EdiSetupModalData,
        private readonly dialog: MatDialogRef<EdiSetupModalComponent, EdiSetupEditorResult>) {
        // Preserve Escape dismissal while keeping backdrop clicks disabled.
        this.keyboard = dialog.keydownEvents().subscribe(event => {
            if (event.key === 'Escape') this.dismiss();
        });
        if (!parameters?.dataItem) { this.dialog.close(); return; }
        this.types = Object.values(parameters.data.ediTypes ?? {});
        this.destinations = Object.values(parameters.data.ediDestinations ?? {});
        if (parameters.argtype === 'cust_no') this.page.custNo = parseInt(parameters.argvalue, 10);
        if (parameters.argtype === 'businessco_no') this.page.businesscoNo = parseInt(parameters.argvalue, 10);
        if (parameters.argtype === 'custgrp_id') this.page.custgrpId = parameters.argvalue;
        this.editing = (parameters.dataItem.p2EdisetupKeyno ?? 0) > 0;
        if (this.editing) {
            for (const field of this.fields) {
                if (parameters.dataItem[field] !== undefined) Object.assign(this.page, { [field]: parameters.dataItem[field] });
            }
            this.page.sendByEmail = parameters.dataItem.sendByEmail === true ? '1' : '0';
            this.page.sendEdi = parameters.dataItem.sendEdi === true ? '1' : '0';
            this.onEdiTypeChanged(this.page.ediTypeKeyno);
        } else {
            this.types = this.types.filter(type => !parameters.rows.some(row => 'ediType' + row.ediTypeKeyno === 'ediType' + type.keyno));
        }
    }

    public onEdiTypeChanged(keyno: number): void {
        this.page.ediTypeKeyno = keyno;
        const type = this.types.find(item => item.keyno === keyno);
        this.reportdefs = Object.values(type?.reportdefs ?? {}).map(item => ({ ...item }));
        this.exportprocs = Object.values(type?.exportprocs ?? {}).map(item => ({ ...item }));
    }

    public async save(): Promise<void> {
        if (this.saving || !this.parameters?.dataItem) return;
        this.saving = true;
        this.error = '';
        try {
            const source = this.parameters.dataItem;
            const candidate: EdiSetupRow = { ...source, dirtyFields: { ...source.dirtyFields } };
            for (const field of this.fields) {
                const value = this.editing && (field === 'sendByEmail' || field === 'sendEdi') ? this.page[field] === '1' : this.page[field];
                if (!this.editing) Object.assign(candidate, { [field]: value });
                else if (source[field] !== undefined && source[field] !== value) {
                    Object.assign(candidate, { [field]: value });
                    candidate.dirtyFields![field] = true;
                    candidate.dirty = true;
                }
            }
            const validation = await this.parameters.validate(candidate);
            if (validation.haserror === true || validation.iserror === true) return;
            Object.assign(source, candidate);
            this.dialog.close({ delete: false });
        } catch (error) {
            this.error = error instanceof Error ? error.message : 'Validation could not be completed.';
        } finally { this.saving = false; }
    }

    public delete(): void { if (this.editing && !this.saving) this.dialog.close({ delete: true }); }
    public dismiss(): void { if (!this.saving) this.dialog.close(); }
    public ngOnDestroy(): void { this.keyboard.unsubscribe(); }
}
